import {
  ShoppingBag,
  Star,
  Heart,
  Eye,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { useState, useEffect, useRef } from "react";
import { useCart } from "../../context/CartContext";
import { useApp } from "../../context/AppContext";
import { useAuth } from "../../context/AuthContext";
import { useWishlist } from "../../context/WishlistContext";

export default function ProductCard({ product }) {
  const { addToCart } = useCart();
  const { setModalProduct, showToast } = useApp();
  const { user } = useAuth();
  const { isWishlisted, toggleWishlist } = useWishlist();
  const [justAdded, setJustAdded] = useState(false);
  const [imgIndex, setImgIndex] = useState(0);
  const intervalRef = useRef(null);
  const [hovered, setHovered] = useState(false);
  const activeImgIndex = hovered ? imgIndex : 0;

  const images = product.images || [];
  const hasMultiple = images.length > 1;

  useEffect(() => {
    if (hovered && hasMultiple) {
      intervalRef.current = setInterval(() => {
        setImgIndex((i) => (i + 1) % images.length);
      }, 1400);
    } else {
      clearInterval(intervalRef.current);
    }
    return () => clearInterval(intervalRef.current);
  }, [hovered, hasMultiple, images.length]);

  const hasVariants = product.variants?.length > 0;
  const lowestPrice = hasVariants
    ? Math.min(...product.variants.map((v) => Number(v.price)))
    : product.price;

  const handleAdd = (e) => {
    e.stopPropagation();
    if (hasVariants) {
      setModalProduct(product);
      return;
    }
    addToCart(product);
    showToast({ name: product.name, image: product.images[0] });
    setJustAdded(true);
    setTimeout(() => setJustAdded(false), 1600);
  };

  const prev = (e) => {
    e.stopPropagation();
    setImgIndex((i) => (i - 1 + images.length) % images.length);
  };

  const next = (e) => {
    e.stopPropagation();
    setImgIndex((i) => (i + 1) % images.length);
  };

  const categoryConfig = {
    birthday: {
      pill: "bg-brand-crimson-50/95 text-brand-crimson-500",
      dot: "#c4354a",
    },
    anniversary: { pill: "bg-rose-50/95 text-rose-400", dot: "#f9a3b4" },
    "mother's day": { pill: "bg-pink-50/95 text-pink-500", dot: "#f472b6" },
    "valentine's day": { pill: "bg-rose-50/95 text-rose-500", dot: "#e879a4" },
    "get well soon": {
      pill: "bg-brand-forest-100/95 text-brand-forest-600",
      dot: "#8d9380",
    },
    "thank you": { pill: "bg-amber-50/95 text-amber-600", dot: "#f4c26a" },
    sympathy: {
      pill: "bg-brand-forest-100/95 text-brand-forest-600",
      dot: "#8d9380",
    },
    "just-because": { pill: "bg-amber-50/95 text-amber-600", dot: "#f4c26a" },
    luxury: { pill: "bg-violet-50/95 text-violet-500", dot: "#b39ddb" },
  };
  // Use original-case first occasion for display label
  const displayCategory = product.occasions?.[0] ?? product.category ?? "other";
  const catCfg = categoryConfig[displayCategory.toLowerCase()] || {
    pill: "bg-brand-cream-100/95 text-brand-forest-400",
    dot: "#ccc",
  };

  const filledStars = Math.floor(product.rating);

  return (
    <div
      className="group flex flex-col h-full"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div
        className="relative flex flex-col h-full bg-brand-cream-50 rounded-3xl overflow-hidden
                   shadow-[0_2px_16px_rgba(0,0,0,0.06)]
                   hover:shadow-[0_16px_48px_rgba(0,0,0,0.13)]
                   transition-all duration-500 cursor-pointer
                   border border-brand-cream-200/80"
        style={{ willChange: "box-shadow, transform" }}
      >
        {/* ── Image area ── */}
        <div
          className="relative w-full overflow-hidden rounded-t-3xl"
          onClick={() => setModalProduct(product)}
          style={{ aspectRatio: "3/4" }}
        >
          {images.map((src, i) => (
            <img
              key={i}
              src={src}
              alt={`${product.name} ${i + 1}`}
              loading="lazy"
              decoding="async"
              className={`absolute inset-0 w-full h-full object-cover transition-all duration-700
                ${i === activeImgIndex ? "opacity-100 scale-100" : "opacity-0 scale-[1.04]"}`}
            />
          ))}

          {/* Ambient color wash at top */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/[0.04] via-transparent to-black/50 z-10 pointer-events-none" />

          {/* Subtle grain texture overlay */}
          <div
            className="absolute inset-0 z-10 pointer-events-none opacity-[0.03]"
            style={{
              backgroundImage:
                "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='1'/%3E%3C/svg%3E\")",
              backgroundSize: "128px",
            }}
          />

          {/* Arrows */}
          {hasMultiple && (
            <>
              <button
                onClick={prev}
                aria-label="Previous image"
                className="absolute left-2.5 top-1/2 -translate-y-1/2 z-30
                           bg-white/95 p-1.5 rounded-full shadow-lg
                           opacity-0 group-hover:opacity-100 -translate-x-1 group-hover:translate-x-0
                           hover:scale-110 transition-all duration-200 backdrop-blur-sm"
              >
                <ChevronLeft className="w-3.5 h-3.5 text-brand-forest-600" />
              </button>
              <button
                onClick={next}
                aria-label="Next image"
                className="absolute right-2.5 top-1/2 -translate-y-1/2 z-30
                           bg-white/95 p-1.5 rounded-full shadow-lg
                           opacity-0 group-hover:opacity-100 translate-x-1 group-hover:translate-x-0
                           hover:scale-110 transition-all duration-200 backdrop-blur-sm"
              >
                <ChevronRight className="w-3.5 h-3.5 text-brand-forest-600" />
              </button>
            </>
          )}

          {/* Image dots — hover only on desktop, hidden on mobile (swipe is enough) */}
          {hasMultiple && (
            <div
              className="absolute bottom-3 left-1/2 -translate-x-1/2 gap-1 z-30
                            hidden md:flex md:opacity-0 md:group-hover:opacity-100 transition-opacity duration-300"
            >
              {images.map((_, i) => (
                <button
                  key={i}
                  onClick={(e) => {
                    e.stopPropagation();
                    setImgIndex(i);
                  }}
                  aria-label={`View image ${i + 1}`}
                  className={`rounded-full transition-all duration-300
                    ${i === activeImgIndex ? "bg-brand-cream-50 w-5 h-1.5" : "bg-white/45 w-1.5 h-1.5 hover:bg-white/75"}`}
                />
              ))}
            </div>
          )}

          {/* Wishlist button — top right */}
          <div className="absolute top-3 right-3 z-20">
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (user) {
                  toggleWishlist(product.id, {
                    id: product.id,
                    bouquet_name: product.name,
                    image_url: product.images?.[0],
                    image_urls: product.images,
                    price: product.price,
                    size_prices: product.size_prices,
                  });
                } else {
                  setModalProduct(product); // prompt sign-in via modal
                }
              }}
              aria-label={
                user
                  ? isWishlisted(product.id)
                    ? `Remove ${product.name} from favourites`
                    : `Save ${product.name} to favourites`
                  : `Sign in to save ${product.name} to favourites`
              }
              className={`p-2 rounded-full backdrop-blur-md shadow-sm transition-all duration-300
                ${
                  isWishlisted(product.id)
                    ? "bg-rose-400 text-white scale-110 shadow-rose-200"
                    : "bg-white/85 text-brand-forest-300 hover:bg-white hover:text-rose-400 hover:scale-105"
                }`}
            >
              <Heart
                className={`w-3.5 h-3.5 transition-all ${isWishlisted(product.id) ? "fill-current" : ""}`}
              />
            </button>
          </div>

          {/* CTA bar — slides up from bottom (desktop only) */}
          <div
            className="hidden md:flex absolute bottom-0 left-0 right-0 z-20 px-3 pb-3 pt-1
                          translate-y-full group-hover:translate-y-0
                          transition-transform duration-300 ease-out
                          gap-2"
          >
            <button
              onClick={(e) => {
                e.stopPropagation();
                setModalProduct(product);
              }}
              aria-label={`View details for ${product.name}`}
              className="flex-1 flex items-center justify-center gap-1.5
                         bg-white/95 backdrop-blur-sm text-brand-forest-700
                         font-body text-xs py-2.5 rounded-xl
                         hover:bg-white transition-colors duration-150 shadow-md
                         border border-white/60 font-medium"
            >
              <Eye className="w-3 h-3" /> View
            </button>
            <button
              onClick={handleAdd}
              className={`flex-[2] flex items-center justify-center gap-1.5
                          font-body text-xs py-2.5 rounded-xl shadow-md
                          transition-all duration-300 font-medium
                ${
                  justAdded
                    ? "bg-brand-forest-500 text-white"
                    : "bg-brand-forest-500 hover:bg-brand-forest-600 text-white"
                }`}
            >
              <ShoppingBag className="w-3 h-3" />
              {hasVariants
                ? "Choose Size"
                : justAdded
                  ? "Added ✓"
                  : "Add to Bouquet"}
            </button>
          </div>
        </div>

        {/* ── Card body ── */}
        <div
          className="px-3 md:px-4 pt-3 md:pt-3.5 pb-3 md:pb-4 cursor-pointer flex flex-col gap-2 flex-1"
          onClick={() => setModalProduct(product)}
        >
          {/* Name + Price row */}
          <div className="flex items-start justify-between gap-2">
            <h3 className="font-display text-brand-forest-800 leading-snug text-base group-hover:text-brand-forest-900 transition-colors line-clamp-2">
              {product.name}
            </h3>
            <span className="shrink-0 text-right">
              <span className="font-body text-base font-bold text-[#5C3D2E]">
                $<span className="font-display">{lowestPrice}</span>
              </span>
              <span className="block font-body text-[10px] font-semibold tracking-wide text-brand-forest-300 opacity-80">
                AUD
              </span>
            </span>
          </div>

          {/* Stars */}
          <div className="flex items-center gap-1">
            <div className="flex gap-px">
              {[...Array(5)].map((_, i) => (
                <Star
                  key={i}
                  className={`w-3 h-3 ${
                    i < filledStars
                      ? "fill-amber-400 text-amber-400"
                      : "fill-stone-100 text-stone-100"
                  }`}
                />
              ))}
            </div>
            <span className="font-body text-xs text-brand-forest-500 font-medium ml-0.5">
              {product.rating}
            </span>
            {/* Sizes or availability dot */}
            <span className="flex items-center gap-1 ml-auto">
              {hasVariants ? (
                <span className="font-body text-xs text-brand-forest-400">
                  {product.variants.map((v) => v.label).join(" · ")}
                </span>
              ) : (
                <>
                  <span
                    className="inline-block w-1.5 h-1.5 rounded-full animate-pulse"
                    style={{ backgroundColor: catCfg.dot }}
                  />
                  <span className="font-body text-xs text-brand-forest-400">
                    Available now
                  </span>
                </>
              )}
            </span>
          </div>

        </div>

        {/* ── Mobile add to cart button — always visible ── */}
        <div className="md:hidden px-3 pb-3">
          <button
            onClick={handleAdd}
            className={`w-full flex items-center justify-center gap-1.5
                        font-body text-xs py-3 rounded-xl
                        active:scale-95 transition-all duration-300 font-semibold shadow-sm
              ${
                justAdded
                  ? "bg-brand-forest-600 text-white"
                  : "bg-brand-forest-500 hover:bg-brand-forest-600 text-white"
              }`}
          >
            <ShoppingBag className="w-3.5 h-3.5" />
            {hasVariants
              ? "Choose Size"
              : justAdded
                ? "Added ✓"
                : "Add to Cart"}
          </button>
        </div>
      </div>
    </div>
  );
}
